// The side sheet (a drawer from the right) and, on phones, a sheet from the bottom. Both slide on the gentle
// spring over a soft scrim. Close with Escape, a click outside, the close button, or by dragging it away on touch.
// Focus moves into the sheet when it opens, stays inside while it is open, and goes back where it was on close.
// Render it inside <AnimatePresence> so it can leave with its exit animation.
import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { motion, useDragControls, useReducedMotion, type PanInfo } from 'motion/react';
import { scrim, sheet, spring } from '../lib/motion';
import { IconX } from './icons';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Sheet({ crumbs, onClose, children, width = 720, side = 'right', label, layoutId, tools }: {
  crumbs: ReactNode; onClose: () => void; children: ReactNode; width?: number; side?: 'right' | 'bottom'; label?: string;
  /** Header controls before the × (a sheet that is the page itself carries the AI button here, 6.2). */
  tools?: ReactNode;
  /** Shared-element transition: the sheet grows out of the element with the same layoutId. */
  layoutId?: string;
}) {
  const panel = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const drag = useDragControls();
  useSheetFocus(panel, onClose);

  const axis = side === 'bottom' ? 'y' : 'x';
  const onDragEnd = (_: unknown, info: PanInfo) => {
    const d = info.offset[axis], v = info.velocity[axis];
    if (d > 120 || v > 600) onClose();
  };
  // Growing out of a row (layoutId): the layout animation moves it, so the slide is left out.
  const v = layoutId ? { initial: { opacity: 0.4 }, animate: { opacity: 1 }, exit: { opacity: 0 } } : sheet[side];

  return (
    <>
      <motion.button type="button" className="scrim" onClick={onClose} aria-label="Close" tabIndex={-1} style={{ border: 0, cursor: 'default' }}
        variants={scrim} initial="initial" animate="animate" exit="exit" />
      <motion.aside ref={panel} className={`sheet top ${side}`} role="dialog" aria-modal="true" aria-label={label}
        style={side === 'right' ? { width: `min(${width}px, 100%)` } : undefined}
        layoutId={layoutId}
        initial={reduce ? { opacity: 0 } : v.initial} animate={reduce ? { opacity: 1 } : { ...v.animate, opacity: 1 }} exit={reduce ? { opacity: 0 } : v.exit}
        transition={spring.gentle}
        drag={reduce ? false : axis} dragControls={drag} dragListener={false} dragConstraints={{ top: 0, left: 0, right: 0, bottom: 0 }}
        dragElastic={side === 'bottom' ? { top: 0.05, bottom: 0.8 } : { left: 0.05, right: 0.8 }} onDragEnd={onDragEnd}>
        <div className="shd" onPointerDown={e => { if (e.pointerType !== 'mouse') drag.start(e); }} style={{ touchAction: 'none' }}>
          {side === 'bottom' && <span className="grab" aria-hidden="true" />}
          <div className="crumbs">{crumbs}</div>
          {tools}
          <button className="ib" onClick={onClose} aria-label="Close"><IconX /></button>
        </div>
        <div className="sbody">{children}</div>
      </motion.aside>
    </>
  );
}

/**
 * Focus moves into the sheet when it opens (to [data-autofocus], else the first control), stays inside while it is
 * open (Tab and Shift Tab wrap), Escape closes it, and focus goes back where it was on close.
 */
/** Open sheets, newest last: only the top one answers Escape and Tab (the assistant panel can open over a sheet). */
const stack: RefObject<HTMLElement | null>[] = [];

/** active false: a docked panel (6.2) that is not modal: no trap, no global Escape (it handles its own). */
export function useSheetFocus(panel: RefObject<HTMLElement | null>, onClose: () => void, active = true) {
  const close = useRef(onClose);
  close.current = onClose;
  // While any sheet is open the body says so (toasts on a phone then move to the top, clear of its buttons).
  useEffect(() => {
    if (!active) return;
    const b = document.body;
    b.dataset.sheets = String(Number(b.dataset.sheets ?? 0) + 1);
    return () => { const n = Number(b.dataset.sheets ?? 1) - 1; if (n > 0) b.dataset.sheets = String(n); else delete b.dataset.sheets; };
  }, [active]);
  useEffect(() => {
    if (!active) return;
    const before = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>('[data-autofocus]') ?? panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus({ preventScroll: true });
    stack.push(panel);
    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== panel) return;
      if (e.key === 'Escape') { e.stopPropagation(); close.current(); return; }
      if (e.key !== 'Tab' || !panel.current) return;
      const els = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(x => x.offsetParent !== null);
      if (!els.length) return;
      const a = els[0]!, z = els[els.length - 1]!;
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); stack.splice(stack.indexOf(panel), 1); before?.focus?.({ preventScroll: true }); };
  }, [active]);
}
